import type { AnswerCitation, AskResponse } from "@shared/types";
import type { InspectorStructuredFields } from "@shared/inspectorAnswer";

export function sourceLabels(indices: number[], citations: AnswerCitation[]) {
  return indices.map((idx) => {
    const citation = citations.find((c) => c.citationIndex === idx);
    if (!citation) return `#${idx}`;
    const location = citation.section ? `§${citation.section}` : citation.page ? `p.${citation.page}` : null;
    return location ? `${citation.documentTitle} · ${location}` : citation.documentTitle;
  });
}

export function InspectorAnswerBlocks({ answer }: { answer: AskResponse & InspectorStructuredFields }) {
  const requirements = answer.technicalRequirements ?? [];
  const checklist = answer.fieldChecklist ?? [];
  if (requirements.length === 0 && checklist.length === 0) return null;

  return (
    <div className="inspector-answer-blocks">
      {requirements.length > 0 && (
        <section className="inspector-answer-section" aria-labelledby={`technical-${answer.auditId}`}>
          <span className="section-label" id={`technical-${answer.auditId}`}>Technical requirements</span>
          <div className="technical-requirements-grid">
            {requirements.map((item, index) => (
              <article className="technical-requirement" key={`${item.label}-${index}`}>
                <div className="technical-requirement-label">{item.label}</div>
                <div className="technical-requirement-value">{item.derivedValue ?? item.value}</div>
                {item.derivedValue && item.value !== item.derivedValue && <div className="technical-requirement-source-value">Source requirement: {item.value}</div>}
                {item.sourceFormula && <div className="technical-requirement-calculation"><strong>Source formula:</strong> {item.sourceFormula}</div>}
                {item.calculation && <div className="technical-requirement-calculation"><strong>Calculation:</strong> {item.calculation}</div>}
                <div className="technical-requirement-sources">Verified by: {sourceLabels(item.citationIndices, answer.citations).join("; ")}</div>
              </article>
            ))}
          </div>
        </section>
      )}

      {checklist.length > 0 && (
        <section className="inspector-answer-section" aria-labelledby={`checklist-${answer.auditId}`}>
          <span className="section-label" id={`checklist-${answer.auditId}`}>Field inspection checklist</span>
          <ol className="field-checklist">
            {[...checklist].sort((a, b) => a.order - b.order).map((item, index) => (
              <li className="field-checklist-item" key={`${item.order}-${index}`}>
                <span className="field-check-box" aria-hidden="true">□</span>
                <div>
                  <div className="field-check-action">{item.action}</div>
                  <div className="field-check-sources">Source: {sourceLabels(item.citationIndices, answer.citations).join("; ")}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
